import moment from 'moment-timezone';

import { NeedRecruitmentCardStatus } from '@/modules/companyNeed/types/recruitment';

import type {
	ExistingInterview,
	ScheduleInterviewSuccessPayload,
} from '@/components/modal/Profile/scheduleInterview/types';
import type {
	RecruitmentWorker,
	WorkerInterview,
} from '@/modules/companyNeed/types';

const INTERVIEW_TIME_FORMATS = ['HHmm', 'HH:mm', 'H:mm'];

const parseInterviewTime = (value?: string) => {
	if (!value) return null;
	const padded = value.length <= 4 ? value.padStart(4, '0') : value;
	const parsed = moment(padded, INTERVIEW_TIME_FORMATS, true);
	return parsed.isValid() ? parsed : null;
};

const getInterviewTimestamp = (interview: WorkerInterview): number => {
	const date = moment(interview.date);
	if (!date.isValid()) return 0;

	const time = parseInterviewTime(interview.start);
	if (time) {
		date.hours(time.hours()).minutes(time.minutes()).seconds(0).milliseconds(0);
	}

	return date.valueOf();
};

export const sortWorkerInterviewsByDateDesc = (
	interviews: WorkerInterview[]
): WorkerInterview[] =>
	[...interviews].sort(
		(left, right) => getInterviewTimestamp(right) - getInterviewTimestamp(left)
	);

export const sortWorkerInterviewsByDateAsc = (
	interviews: WorkerInterview[]
): WorkerInterview[] =>
	[...interviews].sort(
		(left, right) => getInterviewTimestamp(left) - getInterviewTimestamp(right)
	);

export const pickLatestWorkerInterview = (
	interviews: WorkerInterview[]
): WorkerInterview | null =>
	sortWorkerInterviewsByDateDesc(interviews)[0] ?? null;

export const pickUpcomingWorkerInterview = (
	interviews: WorkerInterview[]
): WorkerInterview | null => {
	if (interviews.length === 0) return null;

	const sorted = sortWorkerInterviewsByDateAsc(interviews);
	const startOfToday = moment().startOf('day').valueOf();

	return (
		sorted.find(
			(interview) => getInterviewTimestamp(interview) >= startOfToday
		) ?? sorted[sorted.length - 1]
	);
};

export const mergeWorkerInterview = (
	interviews: WorkerInterview[],
	interview: WorkerInterview
): WorkerInterview[] => {
	const interviewIndex = interviews.findIndex(
		(item) => item.id && item.id === interview.id
	);
	if (interviewIndex < 0) return [interview, ...interviews];

	return interviews.map((item, index) =>
		index === interviewIndex ? interview : item
	);
};

export const extractWorkerInterviews = (worker: {
	interview?: WorkerInterview | WorkerInterview[] | null;
	interviews?: WorkerInterview[] | null;
}): WorkerInterview[] => {
	if (Array.isArray(worker.interviews) && worker.interviews.length > 0) {
		return worker.interviews;
	}
	if (Array.isArray(worker.interview)) {
		return worker.interview;
	}
	if (worker.interview) {
		return [worker.interview];
	}
	return [];
};

export const filterInterviewsForCardStatus = (
	interviews: WorkerInterview[],
	status: NeedRecruitmentCardStatus
): WorkerInterview[] => {
	const expectedType =
		status === NeedRecruitmentCardStatus.INTERVIEW
			? 'AGENCY'
			: status === NeedRecruitmentCardStatus.COMPANY_INTERVIEW
				? 'CLIENT'
				: undefined;
	if (!expectedType) return interviews;

	const matchingType = interviews.filter(
		(interview) => interview.interviewType === expectedType
	);
	if (matchingType.length > 0) return matchingType;

	return interviews.filter((interview) => !interview.interviewType);
};

export const toExistingInterview = (
	interview: WorkerInterview
): ExistingInterview | undefined => {
	if (!interview.id) return undefined;

	return {
		id: interview.id,
		date: interview.date,
		start: interview.start,
		end: interview.end,
		comment: interview.comment,
		contactId: interview.contactId,
		contactName: interview.contactName,
	};
};

const toOptimisticInterviewDate = (date: Date | string) => {
	const parsedDate = new Date(date);
	return Number.isNaN(parsedDate.getTime())
		? String(date)
		: parsedDate.toISOString();
};

export const mapScheduleInterviewPayloadToWorkerInterview = (
	payload: ScheduleInterviewSuccessPayload
): WorkerInterview => ({
	id: payload.interview.id,
	date: toOptimisticInterviewDate(payload.interview.date),
	start: payload.interview.start,
	end: payload.interview.end,
	comment: payload.interview.comment,
	contactId: payload.interview.contactId,
	contactName: payload.contactLabel,
	interviewType: payload.interview.interviewType,
});

export const upsertWorkerInterview = (
	worker: RecruitmentWorker,
	interview: WorkerInterview
): void => {
	const nextInterviews = mergeWorkerInterview(
		extractWorkerInterviews(worker),
		interview
	);

	worker.interviews = nextInterviews;
	worker.interview = pickLatestWorkerInterview(nextInterviews);
};
